import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Anchor, MapPin, Calculator, Sparkles } from 'lucide-react';
import axios from 'axios';
import { usePageTitle } from '../hooks/usePageTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Home() {
  const [featuredYachts, setFeaturedYachts] = useState([]);
  const [guides, setGuides] = useState([]);
  const [scrollY, setScrollY] = useState(0);

  // Set page title
  usePageTitle('');

  useEffect(() => {
    fetchFeaturedYachts();
    fetchGuides();
    
    // Parallax scroll effect
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchFeaturedYachts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/yachts`);
      setFeaturedYachts(response.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching yachts:', error);
    }
  };

  const fetchGuides = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/guides`);
      setGuides(response.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching guides:', error);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section - Cinematic Video Background */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Video Background with Fallback */}
        <div className="absolute inset-0 bg-[#0A192F]">
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-1000"
            style={{
              transform: `translateY(${scrollY * 0.3}px)`,
              transition: 'transform 0.1s ease-out',
            }}
            onLoadedData={(e) => {
              // Fade in video once loaded and playing
              e.target.style.opacity = '1';
              e.target.play().catch(err => console.log('Video autoplay prevented:', err));
            }}
            onCanPlay={(e) => {
              // Ensure smooth fade-in
              e.target.style.opacity = '1';
            }}
          >
            {/* Working video sources from Pexels CDN - First source loads faster */}
            <source 
              src="https://videos.pexels.com/video-files/3571264/3571264-uhd_2560_1440_30fps.mp4" 
              type="video/mp4" 
            />
            <source 
              src="https://videos.pexels.com/video-files/854400/854400-hd_1920_1080_25fps.mp4" 
              type="video/mp4" 
            />
          </video>
          
          {/* Enhanced gradient overlays for text readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A192F]/20 via-[#0A192F]/60 to-[#0A192F]/95"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F]/80 via-transparent to-[#0A192F]/60"></div>
          
          {/* Subtle vignette effect */}
          <div className="absolute inset-0 bg-radial-gradient opacity-50"></div>
        </div>

        {/* Animated shine/shimmer effect */}
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute top-0 right-0 w-1/3 h-full opacity-10"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(212, 175, 55, 0.2), transparent)',
              animation: 'shimmer 10s ease-in-out infinite',
            }}
          ></div>
        </div>

        {/* Hero Content - Enhanced Animation */}
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto" data-testid="hero-section">
          <div className="space-y-8" style={{ animation: 'fadeInUp 1.2s ease-out forwards' }}>
            {/* Badge/Tag */}
            <div className="inline-block">
              <span className="text-xs uppercase tracking-[0.3em] text-gold/90 font-semibold px-4 py-2 border border-gold/30 backdrop-blur-sm">
                Premium Yacht Marketplace
              </span>
            </div>
            
            <h1
              className="text-5xl md:text-7xl lg:text-8xl font-medium tracking-tight leading-[1.1] mb-6"
              style={{ 
                fontFamily: 'Playfair Display, serif',
                textShadow: '0 4px 30px rgba(0,0,0,0.9), 0 2px 10px rgba(0,0,0,0.7)',
              }}
            >
              Discover Elite Yachts<br />
              <span className="text-gold" style={{ 
                textShadow: '0 4px 30px rgba(212, 175, 55, 0.5), 0 2px 10px rgba(0,0,0,0.9)',
              }}>
                & Expert Guidance
              </span><br />
              <span className="text-4xl md:text-5xl lg:text-6xl">Across Southeast Asia</span>
            </h1>
            
            <p className="text-lg md:text-xl lg:text-2xl text-white/95 font-light leading-relaxed max-w-3xl mx-auto" style={{
              textShadow: '0 2px 20px rgba(0,0,0,0.9)',
            }}>
              <span className="text-gold/95 font-normal">Superyachts · Catamarans · Charters</span>
              <br />
              in the Andaman Sea, Raja Ampat, Mergui Archipelago & Beyond
            </p>
            
            <div className="flex flex-col sm:flex-row gap-5 justify-center pt-8">
              <Link to="/listings">
                <Button
                  className="bg-gold text-[#0A192F] hover:bg-[#E5C55D] px-10 py-7 text-sm uppercase tracking-widest rounded-none font-semibold shadow-2xl hover:shadow-gold/50 transition-all duration-300 hover:scale-105"
                  data-testid="hero-browse-btn"
                >
                  Browse Yachts
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
              <Link to="/calculator">
                <Button
                  variant="outline"
                  className="border-2 border-gold/70 text-white hover:bg-gold hover:text-[#0A192F] hover:border-gold px-10 py-7 text-sm uppercase tracking-widest rounded-none font-semibold backdrop-blur-sm transition-all duration-300 hover:scale-105"
                  data-testid="hero-calculator-btn"
                >
                  Estimate Charter Cost
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/40 rounded-full flex items-start justify-center p-2 backdrop-blur-sm">
            <div className="w-1 h-3 bg-gold rounded-full animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Featured Yachts */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 max-w-[1600px] mx-auto" data-testid="featured-yachts-section">
        <div className="text-center mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-gold mb-4">Curated Collection</p>
          <h2 className="text-4xl md:text-5xl font-medium tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Featured Yachts
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {featuredYachts.map((yacht) => (
            <Link
              key={yacht.yacht_id}
              to={`/yacht/${yacht.yacht_id}`}
              className="group relative overflow-hidden glass-light hover:border-gold/30 transition-all duration-500 h-[500px]"
              data-testid={`yacht-card-${yacht.yacht_id}`}
            >
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                style={{ backgroundImage: `url('${yacht.images[0]}')` }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F] via-[#0A192F]/50 to-transparent"></div>
              </div>
              <div className="relative h-full flex flex-col justify-end p-8">
                <p className="text-xs uppercase tracking-[0.2em] text-gold mb-2">{yacht.builder}</p>
                <h3 className="text-2xl font-medium mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {yacht.name}
                </h3>
                <div className="flex items-center gap-4 text-sm text-white/60 mb-4">
                  <span>{yacht.length_ft}ft</span>
                  <span>•</span>
                  <span>{yacht.guest_capacity} guests</span>
                  <span>•</span>
                  <span className="capitalize">{yacht.yacht_type}</span>
                </div>
                <div className="flex items-center gap-2 text-white/60 text-sm mb-4">
                  <MapPin size={14} />
                  <span>{yacht.location}</span>
                </div>
                <p className="text-gold font-semibold">
                  {yacht.charter_weekly_usd
                    ? `$${yacht.charter_weekly_usd.toLocaleString()} / week`
                    : `$${yacht.price_usd?.toLocaleString()}`}
                </p>
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link to="/listings">
            <Button
              variant="ghost"
              className="text-gold hover:text-gold/80 uppercase tracking-widest text-xs"
              data-testid="view-all-yachts-btn"
            >
              View All Yachts <ArrowRight className="ml-2" size={14} />
            </Button>
          </Link>
        </div>
      </section>

      {/* Destinations */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 max-w-[1600px] mx-auto" data-testid="destinations-section">
        <div className="text-center mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-gold mb-4">Explore Waters</p>
          <h2 className="text-4xl md:text-5xl font-medium tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Premier Destinations
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              name: 'Raja Ampat',
              description: 'Indonesia\u2019s pristine coral paradise',
              image: 'https://images.unsplash.com/photo-1738242387985-06d60c466d8b?w=800&q=85',
            },
            {
              name: 'Phuket & Andaman',
              description: 'Thailand\u2019s limestone karsts',
              image: 'https://images.unsplash.com/photo-1709034041623-961b4b682ae0?w=800&q=85',
            },
            {
              name: 'Singapore',
              description: 'Gateway to Southeast Asia',
              image: 'https://images.unsplash.com/photo-1672068246031-d37be8d021ce?w=800&q=85',
            },
          ].map((dest, idx) => (
            <div
              key={idx}
              className="relative aspect-[3/4] overflow-hidden group cursor-pointer"
              data-testid={`destination-card-${idx}`}
            >
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                style={{ backgroundImage: `url('${dest.image}')` }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F] via-[#0A192F]/30 to-transparent"></div>
              </div>
              <div className="relative h-full flex flex-col justify-end p-8">
                <h3 className="text-3xl font-medium mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {dest.name}
                </h3>
                <p className="text-white/60">{dest.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Yacht Finder Quiz CTA */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="yacht-finder-section">
        <div className="max-w-[1200px] mx-auto glass-heavy p-12 md:p-16 text-center relative overflow-hidden">
          {/* Decorative background */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-96 h-96 bg-gold rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-gold rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
          </div>
          
          <div className="relative z-10">
            <div className="w-20 h-20 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-8">
              <Sparkles size={40} className="text-gold" />
            </div>
            <p className="text-xs uppercase tracking-[0.2em] text-gold mb-4">Interactive Tool</p>
            <h2 className="text-4xl md:text-5xl font-medium tracking-tight mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Find Your Perfect Yacht
            </h2>
            <p className="text-lg text-white/70 mb-10 max-w-2xl mx-auto">
              Answer a few simple questions about your preferences, and we'll recommend the ideal vessels from our curated fleet. Takes just 60 seconds.
            </p>
            <Link to="/yacht-finder">
              <Button
                className="bg-gold text-[#0A192F] hover:bg-[#E5C55D] px-12 py-7 text-sm uppercase tracking-widest rounded-none font-semibold shadow-2xl hover:shadow-gold/50 transition-all duration-300 hover:scale-105"
                data-testid="yacht-finder-cta-btn"
              >
                Start the Quiz <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Guides Preview */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 max-w-[1600px] mx-auto" data-testid="guides-preview-section">
        <div className="text-center mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-gold mb-4">Expert Insights</p>
          <h2 className="text-4xl md:text-5xl font-medium tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Latest Guides
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {guides.map((guide) => (
            <Link
              key={guide.guide_id}
              to={`/guide/${guide.guide_id}`}
              className="group"
              data-testid={`guide-card-${guide.guide_id}`}
            >
              <div className="aspect-[4/3] overflow-hidden mb-6">
                <img
                  src={guide.image_url}
                  alt={guide.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
              </div>
              <p className="text-xs uppercase tracking-[0.2em] text-gold mb-2">{guide.category}</p>
              <h3 className="text-xl font-medium mb-3 group-hover:text-gold transition-colors" style={{ fontFamily: 'Playfair Display, serif' }}>
                {guide.title}
              </h3>
              <p className="text-white/60 text-sm leading-relaxed mb-4">{guide.summary}</p>
              <span className="text-white/40 text-xs">{guide.read_time_minutes} min read</span>
            </Link>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link to="/guides">
            <Button
              variant="ghost"
              className="text-gold hover:text-gold/80 uppercase tracking-widest text-xs"
              data-testid="view-all-guides-btn"
            >
              All Guides <ArrowRight className="ml-2" size={14} />
            </Button>
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 max-w-[1600px] mx-auto" data-testid="cta-section">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Charter Calculator CTA */}
          <div className="glass-heavy p-12 text-center">
            <Calculator size={48} className="text-gold mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Plan Your Charter
            </h2>
            <p className="text-lg text-white/80 mb-10 max-w-md mx-auto">
              Use our Charter Calculator to estimate total costs including provisions, fuel, and crew gratuities.
            </p>
            <Link to="/calculator">
              <Button
                className="bg-gold text-[#0A192F] hover:bg-[#E5C55D] px-10 py-6 text-sm uppercase tracking-widest rounded-none"
                data-testid="cta-calculator-btn"
              >
                Calculate Costs
              </Button>
            </Link>
          </div>

          {/* Contact CTA */}
          <div className="glass-heavy p-12 text-center">
            <Anchor size={48} className="text-gold mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Ready to Begin?
            </h2>
            <p className="text-lg text-white/80 mb-10 max-w-md mx-auto">
              Connect with our team to explore Southeast Asia's finest yachts and craft your perfect maritime experience.
            </p>
            <Link to="/contact">
              <Button
                className="bg-transparent border border-gold text-gold hover:bg-gold hover:text-[#0A192F] px-10 py-6 text-sm uppercase tracking-widest rounded-none"
                data-testid="cta-contact-btn"
              >
                Get In Touch
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
